import { useMutation } from "@tanstack/react-query";
import { debounce } from "es-toolkit/compat";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { RewardStatus } from "@/api/types";
import { getNextRewardMilestone } from "@/modules/rewards/nextRewardMilestone";
import { queryClient } from "@/pages/Root";
import { getRewards, postVisitCheckin } from "api/methods";

const checkin = debounce((scannedCode: string) => postVisitCheckin({ scannedCode }), 500, {
  leading: true,
  trailing: false,
});

interface UseCheckinOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const getRewardProgress = async () => {
  const rewards = await queryClient.fetchQuery({ ...getRewards.keyed({}) });

  const futureRewards = rewards.filter((r) => r.status === RewardStatus.NotAvailable);
  const nextRewardMilestone = getNextRewardMilestone(futureRewards);

  return nextRewardMilestone ? nextRewardMilestone.unlockProgressPercent : 0;
};

const useCheckin = (options: UseCheckinOptions = {}) => {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (variables: Parameters<typeof checkin>[0]) => checkin(variables),
    onSuccess: async (_data, scannedCode) => {
      // Default success behavior - navigate to reward checkout
      const rewardProgress = await getRewardProgress();
      const queryString = new URLSearchParams({
        rewardProgress: rewardProgress.toString(),
        comingFromScan: "true",
      }).toString();
      // The scanned location code is passed along so the reward tracker page
      // can offer in-store reward redemption without a second scan.
      navigate(`/cart/checkout/reward?${queryString}`, { state: { scannedCode } });

      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(); // Default reward progress, can be overridden
      }
    },
    onError: (err: any) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(err);
      }
    },
  });

  const checkinUser = useCallback(
    (scannedCode: string) => {
      mutation.mutate(scannedCode);
    },
    [mutation],
  );

  return {
    checkinUser,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
};

export default useCheckin;
